<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { useImpactOfDisabilityStore } from '@/impact-of-disability/impact-of-disability.store'
import { CONTENT_TYPES } from '@/util/access_visibility'
import ConfirmDialog from 'primevue/confirmdialog'
import GbButton from '@/components/GbButton.vue'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import type { LibraryImpactOfDisability } from '@/library/library_types'
import { IOD_SAVING_STATES } from '@/impact-of-disability/impact-of-disability_constants'
import type { SavingState } from '@/impact-of-disability/impact-of-disability_types'
import { transformResourceComponentsToWiki, transformWikiToResourceComponents } from '@/components/editor/wiki_tiptap_helpers'
import { useLibraryStore } from '@/library/library.store.ts'
import TextLinkButton from '@/components/TextLinkButton.vue'
import CopyImpactOfDisabilityModal from '@/impact-of-disability/CopyImpactOfDisabilityModal.vue'
import { toolkitUrls } from '@/util/links.js'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useContentReviewStore } from '@/content-review/content-review.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useContentReview } from '@/content-review/composables/useContentReview'
import { reviewImpactStatementStream } from '@/content-review/api'
import ContentReviewButton from '@/content-review/components/ContentReviewButton.vue'
import ContentReviewModal from '@/content-review/components/ContentReviewModal.vue'
import ContentReviewResults from '@/content-review/components/ContentReviewResults.vue'
import { useImpactStatementReviewAnalytics } from '@/impact-of-disability/composables/useImpactStatementReviewAnalytics'
import { useImpactCriteriaAttrs } from '@/impact-of-disability/composables/useImpactCriteriaAttrs'

const impactOfDisabilityStore = useImpactOfDisabilityStore()
const libraryStore = useLibraryStore()
const visibilityStore = useVisibilityStore()
const contentReviewStore = useContentReviewStore()
const contentProfileStore = useContentProfileStore()

const props = defineProps<{
  statement_uuid: string
  statement: LibraryImpactOfDisability
}>()

const iodEventContext = computed(() => ({
  impact_statement_is_library: true,
  iod_selected_disability: props.statement?.disability,
  iod_selected_subjects: props.statement?.subjects,
  iod_selected_impact_areas: props.statement?.impact_areas?.map(ia => ia.area_label) || [],
  iod_page: 'Saved Statement'
}))

const analytics = getAnalyticsTracker().getChildContext(iodEventContext.value)

const { isReviewing, reviewResults, reviewError, hasResults, startReview, clearResults } = useContentReview()
const showReviewModal = ref(false)

const impactCriteria = computed(() =>
  contentReviewStore.impactCriteriaForProfile(
    contentProfileStore.profileId,
    visibilityStore.betaVisibility
  )
)
const showReviewButton = computed(() =>
  impactCriteria.value.length > 0
)
const { reviewCriteriaSetName, criteriaEventAttrs } = useImpactCriteriaAttrs()

const {
  handleAutoReviewButtonClick: _handleAutoReviewButtonClick,
  handleModalReview,
  handleCloseReviewResults: _handleCloseReviewResults,
  handleReviewResultsToggle,
  handleReceivedReviewResults,
} = useImpactStatementReviewAnalytics(analytics, criteriaEventAttrs, hasResults)

function handleAutoReviewButtonClick() {
  _handleAutoReviewButtonClick(() => { showReviewModal.value = true })
}

function handleCloseReviewResults() {
  _handleCloseReviewResults(() => { clearResults() })
}

function handleReview() {
  handleModalReview(() => {
    showReviewModal.value = false
    startReview(
      ({ signal, onProgress }) =>
        reviewImpactStatementStream({
          impactStatementText: currentStatement.value.statement,
          contentProfile: contentProfileStore.profileId,
          subjects: props.statement.subjects,
          betaMode: visibilityStore.betaVisibility,
          signal,
          onProgress,
        }),
      handleReceivedReviewResults
    )
  })
}

const toEmbeddedText = transformWikiToResourceComponents
const toWikiText = transformResourceComponentsToWiki

const originalStatement = ref<LibraryImpactOfDisability>({} as LibraryImpactOfDisability)
const currentStatement = ref<LibraryImpactOfDisability>({} as LibraryImpactOfDisability)

const isFolderOwner = computed(() => libraryStore.doesUserOwnCurrentFolder)

const openCopySummaryModal = ref(false)

const saveStatus = computed<SavingState>(() => {
  const uuid = props.statement.uuid
  if (!uuid) return IOD_SAVING_STATES.NO_CHANGES
  return (impactOfDisabilityStore.savingStates[uuid] as SavingState) || IOD_SAVING_STATES.NO_CHANGES
})

const userFolderRole = computed(() => {
  return libraryStore.getUserRoleForFolder(props.statement.student_uuid || '') || ''
})

const hasTrackedView = ref(false)

onMounted(() => {
  const initialStatement = props.statement
  originalStatement.value = { ...initialStatement }
  currentStatement.value = { ...initialStatement }
  if (!contentReviewStore.profiles.length) {
    contentReviewStore.fetchProfiles()
  }
})

watch(userFolderRole, (role) => {
  
  if (role && !hasTrackedView.value) {
    analytics.sendEvent(
      EVENT_NAMES.IMPACT_OF_DISABILITY.VIEWED_LIBRARY_STATEMENT,
      { user_folder_role: role }
    )
    hasTrackedView.value = true
  }
}, { immediate: true })

const readOnlyMessageClasses = 'py-2 px-1.5 bg-gray-1000 text-white font-default text-sm font-regular leading-[1.125rem] mt-1'

// Watch for changes in the store's statement and update currentStatement
watch(() => impactOfDisabilityStore.savedStatements.find(s => s.uuid === props.statement.uuid), (storeStatement) => {
  if (storeStatement && props.statement.uuid) {
    currentStatement.value = { ...storeStatement }
  }
}, { deep: true })

// Watch for manual changes to currentStatement and update saving state
watch(currentStatement, (newStatement) => {
  if (props.statement.uuid) {
    if (newStatement.statement !== originalStatement.value.statement) {
      impactOfDisabilityStore.setSavingState(props.statement.uuid, IOD_SAVING_STATES.UNSAVED_CHANGES)
    } else if (!saveStatus.value || saveStatus.value === IOD_SAVING_STATES.UNSAVED_CHANGES) {
      impactOfDisabilityStore.setSavingState(props.statement.uuid, IOD_SAVING_STATES.NO_CHANGES)
    }
  }
}, { deep: true })

const saveChanges = async () => {
  if (!props.statement) return
  
  try {
    await impactOfDisabilityStore.saveStatement(
      props.statement.uuid,
      { statement: currentStatement.value.statement }
    )

    analytics.sendEvent(
      EVENT_NAMES.IMPACT_OF_DISABILITY.SAVED_STATEMENT,
      { user_folder_role: userFolderRole.value, type: CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY }
    )
    
    const updatedStatement = impactOfDisabilityStore.savedStatements.find(s => s.uuid === props.statement.uuid)
    if (updatedStatement) {
      currentStatement.value = { ...updatedStatement }
      originalStatement.value = { ...updatedStatement }
    }
  } catch (error) {
    console.error('Failed to save changes:', error)
  }
}

const discardChanges = async () => {
  if (window.confirm('Are you sure you want to discard any unsaved changes?')) {
    if (props.statement) {
      try {
        await impactOfDisabilityStore.updateStatement(originalStatement.value)
        currentStatement.value = { ...originalStatement.value }
      } catch (err) {
        console.error('Failed to discard changes:', err)
      }
    }
  }
}

const navigateToCompose = () => {
  const url = new URL(toolkitUrls.impactOfDisability(visibilityStore.aiFeatureVisibility, visibilityStore.betaVisibility))
  url.searchParams.set('ref', 'saved_iod')
  window.location.href = url.toString()
}

const handleRemoveEvent = () => {
  analytics.sendEvent(
    EVENT_NAMES.IMPACT_OF_DISABILITY.REMOVED_STATEMENT,
    { user_folder_role: userFolderRole.value }
  )
}

</script>

<template>
  <div v-if="props.statement && libraryStore.currentFolder?.uuid">
    <div class="flex justify-between items-center max-w-3xl mb-4">
      <h2 class="text-text-xl font-medium text-gray-900 pl-8">
        {{ props.statement.disability }}
      </h2>
      <TextLinkButton
        text="Compose a new impact statement"
        :default-underlined="false"
        :custom-class="'mr-1'"
        @click="navigateToCompose"
      />
    </div>
    <div class="flex flex-col lg:flex-row justify-between">
      <div class="relative max-w-3xl grow">
        <div v-if="!isFolderOwner" :class="`${readOnlyMessageClasses} flex flex-nowrap items-center gap-1`">
          <span class="material-icons text-sm">lock</span>
          Only the owner of this Shared Folder can make edits. To make edits,
          <TextLinkButton
            text="copy to your Library"
            text-size="sm"
            icon-name="file_copy"
            icon-style="outlined"
            color="white"
            :default-underlined="true"
            @click="openCopySummaryModal = true"
          />
        </div>

        <CopyTrackWrapper
          type="impactofdisabilitystatement"
          :title="props.statement.disability"
          :uuid="props.statement.uuid"
        >
          <WikiTextEditor
            v-model:wikiText="currentStatement.statement"
            :toEmbeddedText="toEmbeddedText"
            :toWikiText="toWikiText"
            :reset-value="props.statement.statement"
            :outer-class="'rounded-bottom-sm border border-gray-400 px-6 pb-6 mb-4 white-mode' + (isFolderOwner ? '': ' bg-gray-200')"
            edited-text-type="impactofdisabilitystatement"
            :read-only="!isFolderOwner"
            :editor-wrapper-class="'pt-3' + (isFolderOwner ? '': ' bg-gray-200')"
          />
        </CopyTrackWrapper>

        <ContentReviewResults
          v-if="hasResults || isReviewing || reviewError"
          title="Impact Statement Review"
          :results="reviewResults"
          :is-loading="isReviewing"
          :error="reviewError"
          :expected-count="impactCriteria.length"
          @close="handleCloseReviewResults"
          @toggle="handleReviewResultsToggle"
        />
      </div>
      <div class="flex flex-col">

        <div class="w-plw-subcontent-panel ml-4 mb-2">
          <LibraryContentPanel
            :content-uuid="statement.uuid"
            :content-type="CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY"
            :save-status="saveStatus"
            :updated-at="statement.updated_at"
            @save="saveChanges"
            @discard="discardChanges"
            @remove="handleRemoveEvent"
          />

        <ContentReviewButton
          v-if="showReviewButton"
          :criteria-set-name="reviewCriteriaSetName"
          :has-existing-results="hasResults"
          class="my-4"
          @click="handleAutoReviewButtonClick"
        />
        </div>
      </div>
    </div>
  </div>

  <ConfirmDialog v-if="props.statement" :group="`${props.statement.uuid}_${libraryStore.currentFolder?.uuid}`" class="w-auto md:w-[38rem]">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col rounded-md bg-surface-50">
        <div class="mt-2 flex justify-between border-b border-gray-300 p-3">
          <span class="block pl-3 text-xl font-medium">{{ message.header }}</span>
          <GbButton
            size="small"
            rounded
            :iconOnly="true"
            aria-label="close dialog"
            gbTheme="grayTertiary"
            class="mr-4 self-start"
            @click="rejectCallback"
          >
            <span class="material-icons-round">close</span>
          </GbButton>
        </div>
        <p class="py-5 pl-7 pr-12">{{ message.message }}</p>
        <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
          <GbButton
            label="Cancel"
            outlined
            @click="rejectCallback"
            gbTheme="grayOutlineSecondary"
          ></GbButton>
          <GbButton label="Delete" @click="acceptCallback" gbTheme="redPrimary"></GbButton>
        </div>
      </div>
    </template>
  </ConfirmDialog>

  <CopyImpactOfDisabilityModal
    :visible="openCopySummaryModal"
    :content-uuid="props.statement.uuid"
    @update:visible="(val: boolean) => { openCopySummaryModal = val }"
  />

  <ContentReviewModal
    :open="showReviewModal"
    :criteria="impactCriteria"
    :criteria-set-name="reviewCriteriaSetName"
    content-label="Impact Statement"
    description-label="impact of disability statement"
    :subjects="props.statement.subjects"
    @update:open="showReviewModal = $event"
    @review="handleReview"
  />
</template>
